import React, { useState, useEffect, useRef } from 'react';
import { toast } from '../../../components/shared/toast';
import FormSelect from '../../Songs/components/FormSelect';
import { apiClient } from '../../../api/apiClient';
import { GET_OWNERSHIP_DETAILS_URL } from '../../../api/ownership';
import { GET_SONGS_URL } from '../../../api/song';
import './AddDocument.css';

const TAG_OPTIONS = [
  { value: 'Singer', label: 'Singer' },
  { value: 'Lyrics', label: 'Lyrics' },
  { value: 'Musician', label: 'Musician' },
  { value: 'Record Label', label: 'Record Label' }
];

const SONG_OPTIONS = [];

const OWNERSHIP_OPTIONS = [
  { key: 'is_singer', label: 'Singer' },
  { key: 'is_lyrics', label: 'Lyrics' },
  { key: 'is_musician', label: 'Musician' },
  { key: 'is_recordlabel', label: 'Record Label' }
];

function AddDocument({ existingDocument, onSave, onCancel, t }) {
  const isEditing = Boolean(existingDocument);

  const [docName, setDocName] = useState(
    existingDocument ? (existingDocument.name || existingDocument.document_name || '') : ''
  );

  const getInitialTags = () => {
    if (!existingDocument) return [];
    if (existingDocument.is_singer || existingDocument.is_lyrics || existingDocument.is_musician || existingDocument.is_recordlabel) {
      const list = [];
      if (existingDocument.is_singer) list.push('Singer');
      if (existingDocument.is_lyrics) list.push('Lyrics');
      if (existingDocument.is_musician) list.push('Musician');
      if (existingDocument.is_recordlabel) list.push('Record Label');
      return list;
    }
    const rawTags = existingDocument.tags || existingDocument.document_tag || '';
    if (!rawTags) return [];
    if (Array.isArray(rawTags)) return rawTags;
    return String(rawTags).split(',').map(t => t.trim()).filter(Boolean);
  };

  const [selectedTags, setSelectedTags] = useState(getInitialTags);
  const [searchTagQuery, setSearchTagQuery] = useState('');
  const [addToOwnership, setAddToOwnership] = useState(
    existingDocument
      ? (existingDocument.is_ownership !== undefined ? Boolean(existingDocument.is_ownership) : (existingDocument.addToOwnership === 'Yes'))
      : true
  );
  const [uploadedFile, setUploadedFile] = useState(
    existingDocument && (existingDocument.document_url || existingDocument.name)
      ? { name: existingDocument.name || 'document.pdf', size: 1024 * 250 }
      : null
  );

  const getInitialSongs = () => {
    if (!existingDocument) return [];
    if (Array.isArray(existingDocument.song_ids) && existingDocument.song_ids.length > 0) {
      return existingDocument.song_ids.map(String);
    }
    if (Array.isArray(existingDocument.songs)) {
      return existingDocument.songs.map(s => String(s.id || s.song_id));
    }
    return [];
  };

  const getInitialOwnershipTypes = () => {
    if (!existingDocument) return {};
    if (existingDocument.song_ownership_types || existingDocument.song_ownership_flags) {
      return existingDocument.song_ownership_types || existingDocument.song_ownership_flags;
    }
    if (Array.isArray(existingDocument.songs)) {
      const map = {};
      existingDocument.songs.forEach(s => {
        const sId = String(s.id || s.song_id);
        if (sId) {
          map[sId] = {
            is_singer: s.is_singer === 1 || s.is_singer === true ? 1 : 0,
            is_lyrics: s.is_lyrics === 1 || s.is_lyrics === true ? 1 : 0,
            is_musician: s.is_musician === 1 || s.is_musician === true ? 1 : 0,
            is_recordlabel: s.is_recordlabel === 1 || s.is_recordlabel === true || s.is_record_label === 1 ? 1 : 0
          };
        }
      });
      return map;
    }
    return {};
  };

  const [selectedSongs, setSelectedSongs] = useState(getInitialSongs);
  const [songOwnershipTypes, setSongOwnershipTypes] = useState(getInitialOwnershipTypes);
  const [songDetailsMap, setSongDetailsMap] = useState({});
  const [isDragActive, setIsDragActive] = useState(false);

  const fileInputRef = useRef(null);

  // Load existing document songs details if needed
  useEffect(() => {
    if (existingDocument && Array.isArray(existingDocument.songs)) {
      const namesMap = {};
      existingDocument.songs.forEach(s => {
        const sId = String(s.id || s.song_id);
        namesMap[sId] = s.name || s.rawName || s.nameSinhala || `Song ${sId}`;
      });
      setSongDetailsMap(prev => ({ ...prev, ...namesMap }));
    }

    if (existingDocument && existingDocument.id && (!existingDocument.songs || existingDocument.songs.length === 0)) {
      const fetchDocDetails = async () => {
        try {
          const res = await apiClient(GET_OWNERSHIP_DETAILS_URL(existingDocument.id));
          if (res && Array.isArray(res.songs)) {
            const loadedSongIds = res.songs.map(s => String(s.id || s.song_id));
            setSelectedSongs(loadedSongIds);

            const typesMap = {};
            const namesMap = {};
            res.songs.forEach(s => {
              const sId = String(s.id || s.song_id);
              typesMap[sId] = s.ownershipType || s.ownership_type || 'music';
              namesMap[sId] = s.name || s.rawName || s.nameSinhala || `Song ${sId}`;
            });
            setSongOwnershipTypes(typesMap);
            setSongDetailsMap(prev => ({ ...prev, ...namesMap }));
          }
        } catch (err) {
          console.error('Failed to fetch full ownership details:', err);
        }
      };
      fetchDocDetails();
    }
  }, [existingDocument]);

  // Pre-fetch song names for selected songs missing in songDetailsMap
  useEffect(() => {
    const missingIds = selectedSongs.filter(id => !songDetailsMap[String(id)]);
    if (missingIds.length === 0) return;

    const fetchMissing = async () => {
      try {
        const fetched = await Promise.all(
          missingIds.map(async (id) => {
            try {
              const item = await apiClient(`${GET_SONGS_URL}/${id}`);
              if (item) {
                const name = item.nameSinhala ? `${item.nameSinhala} (${item.name})` : item.name;
                return { id: String(item.id), name };
              }
            } catch (e) {
              console.warn(`Failed to fetch song details for ID ${id}:`, e);
            }
            return null;
          })
        );
        const newMap = {};
        fetched.filter(Boolean).forEach(item => {
          newMap[item.id] = item.name;
        });
        if (Object.keys(newMap).length > 0) {
          setSongDetailsMap(prev => ({ ...prev, ...newMap }));
        }
      } catch (err) {
        console.warn('Error fetching missing song titles:', err);
      }
    };
    fetchMissing();
  }, [selectedSongs, songDetailsMap]);

  // File Upload Handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const validateFile = (file) => {
    if (!file) return false;
    if (file.size > 10 * 1024 * 1024) {
      toast.warning('File size must be smaller than 10MB.');
      return false;
    }
    return true;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setUploadedFile({
          file,
          name: file.name,
          size: file.size
        });
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setUploadedFile({
          file,
          name: file.name,
          size: file.size
        });
      }
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleRemoveSong = (songValue) => {
    setSelectedSongs(prev => prev.filter(v => String(v) !== String(songValue)));
    setSongOwnershipTypes(prev => {
      const copy = { ...prev };
      delete copy[songValue];
      return copy;
    });
  };

  const handleOwnershipFlagToggle = (songValue, key) => {
    setSongOwnershipTypes(prev => {
      const current = prev[songValue] || { is_singer: 0, is_lyrics: 0, is_musician: 0, is_recordlabel: 0 };
      return {
        ...prev,
        [songValue]: {
          ...current,
          [key]: current[key] === 1 ? 0 : 1
        }
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!docName.trim()) {
      toast.warning('Please enter document name.');
      return;
    }

    if (selectedSongs.length === 0) {
      toast.warning('Please select at least one song.');
      return;
    }

    const unassignedSongs = selectedSongs.filter(sVal => {
      const flags = songOwnershipTypes[sVal];
      if (!flags) return true;
      return !flags.is_singer && !flags.is_lyrics && !flags.is_musician && !flags.is_recordlabel;
    });

    if (unassignedSongs.length > 0) {
      toast.warning('Please select at least one ownership type for each selected song.');
      return;
    }

    const tagArray = Array.isArray(selectedTags) ? selectedTags : String(selectedTags || '').split(',').map(t => t.trim()).filter(Boolean);
    const tagString = tagArray.join(', ');

    const isSingerTag = tagArray.some(t => String(t).toLowerCase().includes('singer') || String(t).toLowerCase().includes('sing')) ? 1 : 0;
    const isLyricsTag = tagArray.some(t => String(t).toLowerCase().includes('lyric')) ? 1 : 0;
    const isMusicianTag = tagArray.some(t => String(t).toLowerCase().includes('music') || String(t).toLowerCase().includes('musician')) ? 1 : 0;
    const isRecordLabelTag = tagArray.some(t => String(t).toLowerCase().includes('record') || String(t).toLowerCase().includes('label')) ? 1 : 0;

    const payload = {
      id: existingDocument ? existingDocument.id : undefined,
      document_name: docName.trim(),
      name: docName.trim(),
      is_singer: isSingerTag,
      is_lyrics: isLyricsTag,
      is_musician: isMusicianTag,
      is_recordlabel: isRecordLabelTag,
      document_tag: tagString,
      tags: tagString,
      is_ownership: addToOwnership ? 1 : 0,
      addToOwnership: addToOwnership ? 'Yes' : 'No',
      file: uploadedFile ? uploadedFile.file : null,
      document_url: uploadedFile ? (uploadedFile.name || '') : (existingDocument?.document_url || ''),
      song_ids: selectedSongs,
      song_ownership_flags: songOwnershipTypes,
      songs: selectedSongs.map(val => {
        const flags = songOwnershipTypes[val] || { is_singer: 0, is_lyrics: 0, is_musician: 0, is_recordlabel: 0 };
        return {
          id: val,
          song_id: val,
          value: val,
          is_singer: flags.is_singer ? 1 : 0,
          is_lyrics: flags.is_lyrics ? 1 : 0,
          is_musician: flags.is_musician ? 1 : 0,
          is_recordlabel: flags.is_recordlabel ? 1 : 0
        };
      })
    };

    onSave(payload);
  };

  const filteredTagOptions = searchTagQuery.trim()
    ? TAG_OPTIONS.filter(opt => opt.label.toLowerCase().includes(searchTagQuery.toLowerCase()))
    : TAG_OPTIONS;

  return (
    <div className="add-document-wrapper">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-nav">
        <span className="breadcrumb-link" onClick={onCancel}>
          Ownership
        </span>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">
          {isEditing 
            ? (t('ownership.editDocument.breadcrumb') || 'Edit Document')
            : (t('ownership.addDocument.breadcrumb') || 'Add Document')
          }
        </span>
      </div>

      {/* Header Row */}
      <div className="add-document-header-row">
        <div className="header-icon-box">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <line x1="10" y1="9" x2="9" y2="9" />
            <line x1="8" y1="9" x2="8" y2="9" />
          </svg>
        </div>
        <div className="header-text-container">
          <h1 className="add-document-title">
            {isEditing 
              ? (t('ownership.editDocument.title') || 'Edit Document')
              : (t('ownership.addDocument.title') || 'Add Document')
            }
          </h1>
          <p className="add-document-subtitle">
            {t('ownership.addDocument.subtitle') || 'Add and link documents to songs and ownership records.'}
          </p>
        </div>
      </div>

      {/* Form Card */}
      <form onSubmit={handleSubmit} className="form-card">
        {/* Document Name */}
        <div className="form-field-group">
          <label className="form-field-label">
            {t('ownership.addDocument.nameLabel') || 'Document Name'}
            <span className="required-asterisk">*</span>
          </label>
          <input
            type="text"
            className="form-text-input"
            placeholder={t('ownership.addDocument.namePlaceholder') || 'Enter document name'}
            value={docName}
            onChange={(e) => setDocName(e.target.value)}
          />
        </div>

        {/* Document Tags (Multi-select with Singer, Lyrics, Musician, Record Label) */}
        <div className="form-field-group">
          <label className="form-field-label">
            {t('ownership.addDocument.tagsLabel') || 'Document Tags'}
            <span className="required-asterisk">*</span>
          </label>
          
          <div className="document-tags-row">
            {/* Tag Multi-Selection Dropdown */}
            <FormSelect
              label=""
              options={filteredTagOptions}
              value={selectedTags}
              onChange={setSelectedTags}
              isMulti={true}
              placeholder={t('ownership.addDocument.tagsSelectPlaceholder') || 'Select one or more tags'}
            />

            {/* Tag Search Input */}
            <div className="search-input-wrapper">
              <span className="search-icon-inside">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                className="form-text-input"
                placeholder={t('ownership.addDocument.tagsSearchPlaceholder') || 'Search tags'}
                value={searchTagQuery}
                onChange={(e) => setSearchTagQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Add to Ownership Switch */}
        <div className="toggle-section-wrapper">
          <span className="toggle-section-title">
            {t('ownership.addDocument.addToOwnershipLabel') || 'Add to Ownership'}
          </span>
          <span className="toggle-section-subtitle">
            {t('ownership.addDocument.addToOwnershipSub') || 'Link this document to an ownership record.'}
          </span>
          <div className="toggle-control-row">
            <label className="switch">
              <input
                type="checkbox"
                checked={addToOwnership}
                onChange={(e) => setAddToOwnership(e.target.checked)}
              />
              <span className="slider"></span>
            </label>
            <span className="toggle-label-text">
              {t('ownership.addDocument.addToOwnershipLabel') || 'Add to Ownership'}
            </span>
          </div>
        </div>

        {/* Upload Document file input */}
        <div className="upload-container">
          <label className="form-field-label">
            {t('ownership.addDocument.uploadLabel') || 'Upload Document'}
            {!isEditing && <span className="required-asterisk">*</span>}
          </label>
          <span className="toggle-section-subtitle" style={{ margin: '-4px 0 6px 0' }}>
            {t('ownership.addDocument.uploadSub') || 'Upload the document file (PDF recommended)'}
          </span>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <div
            className={`upload-drop-zone ${isDragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
          >
            <div className="upload-content-wrapper">
              <div className="upload-icon-circle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <p className="upload-main-text">
                {t('ownership.addDocument.dragDropText') || 'Drag & drop your file here or'}
              </p>
              <span className="upload-or-text">or</span>
              <button type="button" className="choose-file-btn">
                {t('ownership.addDocument.chooseFile') || 'Choose File'}
              </button>
            </div>
          </div>

          {/* Uploaded File Detail Preview */}
          {uploadedFile && (
            <div className="uploaded-file-card">
              <div className="file-info-left">
                <span className="file-icon-red">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </span>
                <div>
                  <span className="file-name-text">{uploadedFile.name}</span>
                  <span className="file-size-text">{formatFileSize(uploadedFile.size)}</span>
                </div>
              </div>
              <button
                type="button"
                className="remove-file-action"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile();
                }}
                aria-label="Remove uploaded file"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Select Songs Dropdown (Lazy load real songs from BE) */}
        <div className="form-field-group">
          <FormSelect
            label={t('ownership.addDocument.selectSongsLabel') || 'Select Songs'}
            required={true}
            isLazy={true}
            lazyType="songs"
            options={SONG_OPTIONS}
            value={selectedSongs}
            onChange={setSelectedSongs}
            excludeIds={selectedSongs}
            isMulti={true}
            placeholder={t('ownership.addDocument.selectSongsPlaceholder') || 'Search and select songs'}
          />
          <span className="toggle-section-subtitle" style={{ margin: '-4px 0 0 0' }}>
            {t('ownership.addDocument.selectSongsSub') || 'Search and select songs to link with this document.'}
          </span>
        </div>

        {/* Selected Songs List & Table */}
        {selectedSongs.length > 0 && (
          <div className="selected-songs-section">
            <div>
              <span className="section-title">
                {t('ownership.addDocument.selectedSongsLabel') || 'Selected Songs'}
              </span>
              <p className="section-subtitle">
                {t('ownership.addDocument.selectedSongsSub') || 'Set ownership type for each selected song'}
              </p>
            </div>

            <div className="selected-songs-table-container">
              {/* Table Headers */}
              <div className="songs-table-grid songs-table-header">
                <div>{t('ownership.addDocument.colNum') || '#'}</div>
                <div>{t('ownership.addDocument.colSongName') || 'Song Name'}</div>
                <div>{t('ownership.addDocument.colOwnershipType') || 'Ownership Type'} <span className="required-asterisk" style={{ color: '#ef4444' }}>*</span></div>
                <div style={{ textAlign: 'center' }}>
                  {t('ownership.addDocument.colAction') || 'Action'}
                </div>
              </div>

              {/* Table Rows */}
              <div className="songs-table-body">
                {selectedSongs.map((songVal, index) => {
                  const songName = songDetailsMap[String(songVal)] || `Song ${songVal}`;

                  return (
                    <div key={songVal} className="songs-table-grid songs-table-row">
                      <div className="song-index-col">{index + 1}</div>
                      <div className="song-name-col">{songName}</div>
                      <div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                          {OWNERSHIP_OPTIONS.map(opt => {
                            const flags = songOwnershipTypes[songVal] || {};
                            const isChecked = flags[opt.key] === 1 || flags[opt.key] === true;
                            return (
                              <label 
                                key={opt.key} 
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  border: `1px solid ${isChecked ? '#3b82f6' : '#cbd5e1'}`,
                                  backgroundColor: isChecked ? '#eff6ff' : '#ffffff',
                                  color: isChecked ? '#1d4ed8' : '#475569',
                                  fontSize: '13px',
                                  fontWeight: isChecked ? '600' : '500',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleOwnershipFlagToggle(songVal, opt.key)}
                                  style={{ cursor: 'pointer', accentColor: '#3b82f6', width: '14px', height: '14px' }}
                                />
                                <span>{opt.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div className="song-action-col">
                        <button
                          type="button"
                          className="song-delete-btn"
                          onClick={() => handleRemoveSong(songVal)}
                          aria-label="Remove song"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Showing songs count */}
            <div className="showing-songs-pagination-text">
              {t('ownership.addDocument.showingSongs')
                .replace('{count}', selectedSongs.length)
                .replace('{total}', selectedSongs.length)
                .replace('{songText}', selectedSongs.length === 1 
                  ? (t('ownership.addDocument.songWordSingular') || 'song')
                  : (t('ownership.addDocument.songWordPlural') || 'songs')
                )
              }
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="form-actions-footer">
          <button
            type="button"
            className="form-btn btn-outlined"
            onClick={onCancel}
          >
            {t('ownership.addDocument.cancel') || 'Cancel'}
          </button>
          <button
            type="submit"
            className="form-btn btn-primary"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            <span>
              {isEditing 
                ? (t('ownership.editDocument.saveDocument') || 'Update Document')
                : (t('ownership.addDocument.saveDocument') || 'Save Document')
              }
            </span>
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddDocument;
